import React, { useState, useEffect } from 'react';
// import { SortableHandle } from 'react-sortable-hoc';

import { Input as AntInput, Form, Select } from 'antd';

import {
  ColorSelector as _ColorSelector,
  IconSelector as _IconSelector,
  ExtendableTextArea as _TextArea,
  ValueDriverNumberInput,
  ValueDriverSelect,
  MannualCheckbox as _Checkbox,
  PermissionCheckbox as PerCheckbox,
  MainSectionInput as SectionInput,
} from '@components';
import alert from '@components/alert';
import TextArray from '@components/textArray';
import SimpleInput from '@components/simpleInput';
import RichEditor from '../formList/richTextCell/richTextCell';
import { FixedRatios, typeTextMap, INTEGRATION_TYPES } from '@model';
import SectionList from '@components/sectionList/SectionList';
import DeleteIcon from '@components/deleteIcon/DeleteIcon';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const FormItem = Form.Item;
const { Option } = Select;

const formItemWrapper =
  (WrappedComp, { formConfig, inputConfig } = {}) =>
  ({ inputProps = {}, field = {}, formInstance: form, ...props }) => {
    const dependencies = Array.isArray(props.name) ? props.name : [props.name];
    const rowType = inputProps.formInstance?.getFieldValue([
      inputProps.rowName,
      inputProps.rowIndex,
    ])?.type;

    if (
      rowType === 'textContainer' &&
      props.name.includes('link') &&
      Array.isArray(props.rules)
    ) {
      props.rules = [];
    }
    return (
      <FormItem
        {...field}
        dependencies={dependencies}
        {...formConfig}
        {...props}
        // trigger="onBlur"
        // validateTrigger="onBlur"
      >
        <WrappedComp {...inputConfig} {...inputProps} />
      </FormItem>
    );
  };

const getInt = (e) => {
  const value = e?.target.value;
  if (!value) {
    return null;
  }

  return Math.floor(Math.abs(String(value).replace(/[^0-9]/g, ''))) || null;
};

const Input = formItemWrapper(AntInput);

const PureText = ({
  inputProps = {},
  field = {},
  formInstance: form,
  ...props
}) => {
  const rowType = inputProps.formInstance?.getFieldValue([
    inputProps.rowName,
    inputProps.rowIndex,
  ])?.type;
  const typeText =
    (inputProps.config?.addModuleConfig?.typeTextMap || typeTextMap).filter(
      (item) => item.value === rowType,
    )[0]?.typeText || 'Other';
  // debugger;

  return <span>{typeText}</span>;
};

const RichText = (field) =>
  formItemWrapper(RichEditor, { inputConfig: { typeField: field } });

const InputNumber = formItemWrapper(AntInput, {
  formConfig: { getValueFromEvent: getInt, validateTrigger: '' },
  inputConfig: { style: { textAlign: 'center' } },
});

const Checkbox = formItemWrapper(_Checkbox, {
  formConfig: {
    valuePropName: 'checked',
  },
});

const PermissionCheckbox =
  (permissionType) =>
  ({ inputConfig, inputProps, ...restProps }) => {
    const { field = {}, formInstance: form, ...props } = restProps;
    return (
      <FormItem shouldUpdate={true}>
        {({ getFieldValue }) => (
          <Form.Item {...field} {...props}>
            <PerCheckbox
              {...inputConfig}
              {...inputProps}
              permissionType={permissionType}
            />
          </Form.Item>
        )}
      </FormItem>
    );
  };

const MainSectionInput = ({ inputConfig, inputProps, ...restProps }) => {
  const { field = {}, formInstance: form, ...props } = restProps;

  return (
    <FormItem shouldUpdate={true}>
      {({ getFieldValue }) => (
        <Form.Item {...field} {...props}>
          <SectionInput {...inputConfig} {...inputProps} />
        </Form.Item>
      )}
    </FormItem>
  );
};

const TextArea = formItemWrapper(_TextArea, {
  inputConfig: {
    autoSize: true,
  },
});

const ValueDriverWrappedNumberInput = formItemWrapper(ValueDriverNumberInput);

const ValueDriverWrappedSelect = formItemWrapper(ValueDriverSelect, {
  inputConfig: {
    size: 'large',

    suffixIcon: <span className="appkiticon icon-down-chevron-fill" />,
  },
});

const textArray = formItemWrapper(TextArray);

const simpleInput = formItemWrapper(SimpleInput);

const ColorSelector = formItemWrapper(_ColorSelector);

const IconSelector = formItemWrapper(_IconSelector);

export const deleteConfirm = (config = {}) => {
  alert.confirm({
    ...config,
  });
};
export const customDeleteConfirm = (config = {}) => {
  alert.customConfirm({
    ...config,
  });
};
export const noSaveConfirm = (config = {}) => {
  alert.confirmWithNoSave({
    ...config,
  });
};

const DeleteAction = ({
  inputProps: { onRowDelete, alertContent, customEle } = {},
}) => {
  const handleDelete = (close) => {
    onRowDelete && onRowDelete();
    close();
  };

  const confirmDelete = (e) => {
    alertContent
      ? deleteConfirm({ onOk: handleDelete, content: alertContent })
      : deleteConfirm({ onOk: handleDelete });
  };
  return customEle ? (
    customEle({ confirmDelete })
  ) : (
    <DeleteIcon onClick={confirmDelete} />
  );
};

// const DragAction = SortableHandle(
//   ({ inputProps: { onDragHandlerClick } = {} }) => {
//     const handleClick = (e) => {
//       e.stopPropagation();

//       onDragHandlerClick && onDragHandlerClick();
//     };
//     return (
//       <span
//         onMouseDown={handleClick}
//         className="appkiticon icon-menu-outline"
//       />
//     );
//   },
// );

// Replace SortableHandle with a custom DragHandle component
const DragHandle = ({ onDragHandlerClick }) => (
  <span
    className="appkiticon icon-menu-outline"
    onMouseDown={(e) => {
      e.stopPropagation();
      onDragHandlerClick && onDragHandlerClick();
    }}
  />
);

const DragAction = ({ inputProps: { onDragHandlerClick } = {} }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: 'draggable-item' });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <DragHandle onDragHandlerClick={onDragHandlerClick} />
    </div>
  );
};

const INPUT_TYPE = {
  INPUT: 'input',
  NUMBER: 'number',
  COLORSELECTOR: 'color',
  TEXT: 'text',
  CHECK_BOX: 'check',
  DELETE: 'delete',
  DRAG: 'drag',
  SELECT: 'select',
  VALUE_DRIVER_NUMBER: 'valuedrivernumber',
  TEXT_ARRAY: 'textArray',
  SIMPLE_INPUT: 'simpleInput',
  ICON_SELECTOR: 'icon',
  CLIENT_BOX: 'clientCheckbox',
  INTERNAL_BOX: 'internalCheckbox',
  GROUP_BOX: 'groupCheckbox',
  MAIN_SECTION: 'mainSetionInput',
  SECTION_LIST: 'originPermissions',
  PURE_TEXT: 'pureText',
  RICH_TEXT: 'richText',
  RICH_TITLE: 'richTitle',
  PURE_SELECT: 'pureSelect',
};

function DeferredInput(
  { onChange, value: outsideValue, formChange = () => {}, ...props },
  ref,
) {
  const [value, setValue] = useState(outsideValue);
  useEffect(() => {
    setValue(outsideValue);
  }, [outsideValue]);

  return (
    <AntInput
      {...props}
      value={value}
      ref={ref}
      onChange={(e) => {
        formChange();
        setValue(e.target.value);
      }}
      onBlur={(e) => onChange(value)}
    />
  );
}

const INPUT_COMPS = {
  [INPUT_TYPE.INPUT]: formItemWrapper(DeferredInput),
  [INPUT_TYPE.NUMBER]: InputNumber,
  [INPUT_TYPE.COLORSELECTOR]: ColorSelector,
  [INPUT_TYPE.TEXT]: TextArea,
  [INPUT_TYPE.CHECK_BOX]: Checkbox,
  [INPUT_TYPE.CLIENT_BOX]: PermissionCheckbox('clientPermissions'),
  [INPUT_TYPE.INTERNAL_BOX]: PermissionCheckbox('internalPermissions'),
  [INPUT_TYPE.GROUP_BOX]: PermissionCheckbox('groupPermissions'),
  [INPUT_TYPE.MAIN_SECTION]: MainSectionInput,
  [INPUT_TYPE.SECTION_LIST]: formItemWrapper(SectionList),
  [INPUT_TYPE.DELETE]: DeleteAction,
  [INPUT_TYPE.DRAG]: DragAction,
  [INPUT_TYPE.SELECT]: ValueDriverWrappedSelect,
  [INPUT_TYPE.VALUE_DRIVER_NUMBER]: ValueDriverWrappedNumberInput,
  [INPUT_TYPE.TEXT_ARRAY]: textArray,
  [INPUT_TYPE.SIMPLE_INPUT]: simpleInput,
  [INPUT_TYPE.ICON_SELECTOR]: IconSelector,
  [INPUT_TYPE.PURE_TEXT]: PureText,
  [INPUT_TYPE.RICH_TEXT]: RichText({
    underline: true,
    bold: true,
    italic: true,
    unordered_list: true,
    ordered_list: true,
    link: true,
    text_color: true,
  }),
  [INPUT_TYPE.RICH_TITLE]: RichText({
    underline: true,
    bold: true,
    italic: true,
    unordered_list: false,
    ordered_list: false,
    link: true,
    text_color: true,
  }),
  [INPUT_TYPE.PURE_SELECT]: formItemWrapper(
    ({
      options = [],
      shouldRefRowData = false,
      onChange = () => {},
      formChange = () => {},
      ...props
    }) => {
      let value = props.value;

      const type = props.formInstance?.getFieldValue([
        props.rowName,
        props.rowIndex,
      ])?.type;
      const isIframe = props.formInstance?.getFieldValue([
        props.rowName,
        props.rowIndex,
      ])?.iframe;

      let disabled;

      if (['text'].includes(type) && isIframe === false) {
        disabled = true;
      } else {
        disabled = false;
      }

      if (['regular'].includes(type) && isIframe) {
        disabled = false;
      } else if (['regular'].includes(type) && !isIframe) {
        disabled = true;
      }

      if (
        ['other', 'pdf', 'imageModule', 'image', INTEGRATION_TYPES.HTML].includes(
          type,
        )
      ) {
        disabled = false;
      }

      if (['multiSection', 'widget', 'chart', 'textContainer'].includes(type)) {
        disabled = true;
      }

      if (!value && !props.disabled) {
        if (type === 'powerbiModule' || type === 'powerbi') {
          value = FixedRatios.AUTO;
        } else {
          value = FixedRatios.FULL;
        }
      }

      if (props.disabled) {
        value = FixedRatios.NONE;
      }

      if (disabled) {
        value = 'Not Applicable';
      }

      value =
        {
          '64:27': FixedRatios.FULL,
          '16:9': FixedRatios.STANDARD,
          '32:7': FixedRatios.BANNER,
          AUTO: FixedRatios.AUTO,
        }[value] || value;

      const onCombineChange = (...args) => {
        onChange(...args);
        formChange();
      };

      return (
        <Select
          dropdownClassName="role-select-dropdown"
          {...props}
          onChange={onCombineChange}
          dropdownMatchSelectWidth={330}
          value={value}
          disabled={disabled || props.disabled}
          key={`${props.rowName}-${props.rowIndex}-${isIframe}`}
        >
          {type === 'powerbiModule' || type === 'powerbi' ? (
            <Option value={FixedRatios.AUTO} key={`${FixedRatios.AUTO}-${3}`}>
              <div className="role-name">Auto</div>
              <div className="role-description">
                Fit the report to fill the available space
              </div>
            </Option>
          ) : null}
          {options.map((o, i) => (
            <Option value={o.value} key={`${o.value}-${i}`}>
              <div className="role-name">{o.label}</div>
              <div className="role-description">{o.description}</div>
            </Option>
          ))}
        </Select>
      );
    },
    { formConfig: { shouldUpdate: true } },
  ),
};

export { INPUT_TYPE, INPUT_COMPS };
